import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NivaLogo from '../components/NivaLogo';
import { cleanTranscript, mergeHandoverVoiceNote } from '../services/openAIService';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { normalizeHandoverDraft } from '../utils/shiftHandoverCompiler';
import { ROUTES } from '../constants/routes';

export default function ShiftHandoverVoiceGeneratingScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { shiftHandoverDrafts, updatePatientHandoverDraft } = useSession();
  const [statusText, setStatusText] = useState(t('mergingVoiceNote'));
  const hasStartedRef = useRef(false);

  const transcript = location.state?.transcript || '';
  const patientKey = location.state?.patientKey;
  const existingDraft = shiftHandoverDrafts.find((d) => d.patientKey === patientKey);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    let cancelled = false;

    async function run() {
      if (!existingDraft || !transcript.trim()) {
        navigate(ROUTES.SHIFT_HANDOVER, { replace: true });
        return;
      }

      try {
        setStatusText(t('translating'));
        const cleaned = await cleanTranscript(transcript);
        setStatusText(t('mergingVoiceNote'));

        const merged = await mergeHandoverVoiceNote(
          {
            narrative:
              existingDraft.narrative
              || [
                existingDraft.sbar?.situation,
                existingDraft.sbar?.background,
                existingDraft.sbar?.assessment,
                existingDraft.sbar?.recommendation,
              ]
                .map((value) => String(value || '').trim())
                .filter(Boolean)
                .join('\n\n'),
            outstanding_tasks: existingDraft.outstanding_tasks,
            pending_results: existingDraft.pending_results,
            medications_due_next_shift: existingDraft.medications_due_next_shift,
            flags: existingDraft.flags,
          },
          cleaned || transcript
        );

        if (cancelled) return;

        const normalized = normalizeHandoverDraft(
          merged,
          existingDraft,
          existingDraft.outstanding_task_details || []
        );
        updatePatientHandoverDraft(patientKey, {
          ...normalized,
          patientKey: existingDraft.patientKey,
          patientId: existingDraft.patientId,
          patientName: existingDraft.patientName,
          patientRoom: existingDraft.patientRoom,
          outstanding_task_details: existingDraft.outstanding_task_details || [],
        });
      } catch (error) {
        console.error('Voice merge failed:', error);
      }

      if (!cancelled) {
        navigate(ROUTES.SHIFT_HANDOVER, { replace: true });
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [existingDraft, navigate, patientKey, t, transcript, updatePatientHandoverDraft]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page px-6">
      <NivaLogo className="mb-10" />
      <div className="spinner mb-4 h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary" />
      <p className="text-center text-sm font-medium text-gray-600">{statusText}</p>
    </div>
  );
}
